/**
 * Shared domain vocabulary for the ARM (web), the TSD app (React Native) and generated clients.
 * Everything here mirrors the backend: sections, statuses, priorities, meta descriptions, TSD screens.
 */

import type { MapDock } from "./docks";

export interface Section {
  code: string;
  label: string;
}

/** Main menu sections in the order of the source system's command interface. */
export const SECTIONS: Section[] = [
  { code: "inbound", label: "Поступление" },
  { code: "outbound", label: "Отгрузка" },
  { code: "inventory", label: "Остатки и внутренние операции" },
  { code: "resources", label: "Управление ресурсами" },
  { code: "yard", label: "Управление двором" },
  { code: "billing", label: "Ответственное хранение" },
  { code: "costs", label: "Затраты" },
  { code: "integration", label: "Интеграция" },
  { code: "nsi", label: "Справочники" },
  { code: "topology", label: "Топология и процессы" },
  { code: "analytics", label: "Аналитика" },
  { code: "admin", label: "Администрирование" },
];

export type TaskStatus =
  | "new"
  | "planned"
  | "in_list"
  | "ready"
  | "received"
  | "in_progress"
  | "unloading"
  | "waiting"
  | "done"
  | "blocked"
  | "cancelled"
  | "problem";

export interface StatusInfo {
  label: string;
  /** One of six status colours shared by ARM, TSD and reports. */
  color: "neutral" | "info" | "progress" | "success" | "warning" | "danger";
}

export const TASK_STATUSES: Record<TaskStatus, StatusInfo> = {
  new: { label: "Новая", color: "neutral" },
  planned: { label: "Спланирована", color: "info" },
  in_list: { label: "Включена в лист", color: "info" },
  ready: { label: "К выполнению", color: "info" },
  received: { label: "Получена", color: "progress" },
  in_progress: { label: "Выполняется", color: "progress" },
  unloading: { label: "Раскладывание", color: "progress" },
  waiting: { label: "Ожидание", color: "warning" },
  done: { label: "Выполнена", color: "success" },
  blocked: { label: "Заблокирована", color: "warning" },
  cancelled: { label: "Отменена", color: "neutral" },
  problem: { label: "Проблема", color: "danger" },
};

export type Priority = "critical" | "high" | "normal" | "low" | "idle";

export const PRIORITIES: Record<Priority, StatusInfo> = {
  critical: { label: "Критичный", color: "danger" },
  high: { label: "Важный", color: "warning" },
  normal: { label: "Обычный", color: "neutral" },
  low: { label: "Низкий", color: "neutral" },
  idle: { label: "В свободное время", color: "neutral" },
};

/** Field description returned by GET /{entity}/meta. */
export interface FieldMeta {
  name: string;
  label: string;
  /** entity = the name of a registry list (shown by its label); refcode = a reference by code instead of id. */
  type: "string" | "number" | "boolean" | "datetime" | "date" | "uuid" | "json" | "ref" | "multiref" | "password" | "enum" | "multienum" | "entity" | "refcode";
  ref?: string | null;
  required: boolean;
  readonly: boolean;
  hidden: boolean;
  max_length?: number | null;
  options?: FieldOption[] | null;
  /** ref: the list is filtered by these fields of the same form (room -> aisle), WMS-03. */
  depends_on?: string[];
  /** Plain-language help shown under the field. */
  description?: string | null;
  /** Lower bound for numbers (0 for quantities), BUG-01; min_exclusive = the bound itself is not allowed. */
  min?: number | null;
  min_exclusive?: boolean;
  /** UXW-60/64: the hard upper bound the server checks (`max_exclusive` — the bound itself is not allowed). */
  max?: number | null;
  max_exclusive?: boolean;
  /** UXW-60: a whole-number column; `scale` — digits after the comma of a decimal column. */
  integer?: boolean;
  scale?: number | null;
  /** Rarely needed technical field: folded under «Дополнительно» in the form (UX-03). */
  advanced?: boolean;
  /** Reference whose entity depends on another field of the form (dock slot → receipt or order). */
  ref_by?: { field: string; map: Record<string, string> } | null;
  /** Static condition on the referenced list (orders in allowed statuses only). */
  filter?: Record<string, unknown> | null;
  /** BUG-R40-004: the hint of this quantity names the unit of the product chosen in that field. */
  unit_from?: string | null;
  /** BUG-R42-001: saving a changed filled value asks first; {old}, {new} and {code} are filled in. */
  change_warning?: string | null;
  /** Value the server stores when the field is not sent; a new form starts with it (N10). */
  default?: string | number | boolean | null;
  /** UX-forms (ТЗ 29.10.2026): what to enter, where the default comes from, what it affects — the «(?)» of the field. */
  help?: string | null;
  /** Width class of the control (section 5.1): a layout hint, never a limit of the data. */
  width?: "xs" | "s" | "m" | "l" | "xl";
  /** Key of EntityMeta.groups the field is shown in; none = the main block. */
  group?: string | null;
  placeholder?: string | null;
  /** ref: «+» inside the open list creates a row of the referenced catalogue and selects it (UI-15). */
  create?: boolean;
  /** The value follows the context (the one organization of the installation), section 6.3: the filter names the candidates. */
  context?: { filter?: Record<string, unknown> | null } | null;
  /** ref split into plain modes by a boolean of the referenced row («Доставка» / «Самовывоз», UI-11). */
  mode?: { field: string; options: { value: boolean; label: string }[]; hides?: string[] } | null;
  /** UX3010-11: the server sets the value (working organization, side of the contract); the card shows it as a fixed line at the top. */
  static?: boolean;
  /** UX3010-10/13: a new record starts with the working organization of the session. */
  working_default?: boolean;
  /** UX3010-06: the server numbers a new record when the field is left empty («Номер присвоится автоматически»). */
  auto_number?: boolean;
  /** KZ 30.10.2026 (ТЗ реквизитов §5): «radio» — the form ТОО / ИП as two radio buttons. */
  widget?: "radio" | null;
  /** The label (and help) follow another field of the form: «БИН» for a ТОО, «ИИН» for an ИП. */
  label_by?: { field: string; map: Record<string, string>; default?: string; help?: Record<string, string> } | null;
  /** §5.2: a change of this field while `field` is filled shows the warning of the new value; nothing changes by the side. */
  notice_on_change?: { field: string; map: Record<string, string> } | null;
  /** The value a new form starts with (the server stores nothing when it is not sent). */
  initial?: string | number | boolean | null;
  /** The quick check the form repeats for convenience; the server decides. */
  check?: "tax_id" | "iik" | "bic" | "kbe" | null;
  /** A new record from the form must have it; a saved record and the API may leave it empty. */
  required_new?: boolean;
  /**
   * CW31-B06: how each field of `depends_on` filters the referenced list when not by a column of the same name; the
   * first filled field of `depends_on` wins (the owner of the goods, else the recipient). CW31-B22: `via` — through
   * the row chosen in the source field (the order of a cargo unit).
   */
  depends_rules?: Record<string, DependsRule> | null;
}

/** CW31-B06 / B22: one source field of a dependent reference — see FieldMeta.depends_rules. */
export interface DependsRule {
  /** The column of the referenced list the value filters; absent — the column of the same name. */
  target?: string;
  /** The field of the row chosen in the source whose value filters (cargo unit → its `order_id`). */
  via?: string;
  /** The words of the control while this source filters («Договор с владельцем товара»). */
  placeholder?: string;
  /** The words of an empty list; {value} — the name of the source value («Для владельца «…» договоров нет»). */
  empty?: string;
  /** Why a value that does not fit the changed source was cleared; {value} — the name of the new source value. */
  reset?: string;
}

/** CW31-B08: per status, the fields a plain save may change; the others are fixed — the server guard reads the same. */
export interface EditPolicy {
  /** status → fields; "*" — every other status. */
  editable: Record<string, string[]>;
  /** «Реквизиты зафиксированы: …» */
  hint: string;
  /** status (or "*") → how to correct the fixed requisites, in plain words. */
  fix?: Record<string, string>;
}

/** An option of an enum: a historical one (`legacy`) is shown only on a record that already has it. */
export interface FieldOption {
  value: string;
  label: string;
  legacy?: boolean;
  help?: string;
}

/** UX3010-13: one of our organizations («Мои организации») the user may work in. */
export interface CompanyRef {
  id: string;
  code: string;
  name: string;
}

/** UX3010-15: GET /warehouses/{id}/capacity — the physical warehouse once, the occupancy of every organization. */
export interface WarehouseCapacity {
  warehouse_id: string;
  warehouse: string;
  area_m2: string | number | null;
  locations_total: number;
  locations_occupied: number;
  locations_incoming: number;
  locations_blocked: number;
  locations_free: number;
  percent: number;
  volume_capacity_m3: string | number | null;
  volume_used_m3: string | number;
  weight_capacity_kg: string | number | null;
  weight_used_kg: string | number;
  /** Our organizations the warehouse serves; null — every organization. */
  companies: string[] | null;
  note: string;
}

export interface ChildMeta {
  entity: string;
  fk: string;
  label: string;
  /** Feature option that must be enabled for the tab to show. */
  option?: string | null;
  /** UI-19: what the tab holds, shown by the «(?)» next to its name. */
  help?: string | null;
  /** The right to read the tab's list; without it the tab is not shown. */
  permission?: string | null;
  /** UXW-39: "stock" — next to «Создать» the tab offers «Добавить товары» from the stock (option use_stock_picker). */
  picker?: string | null;
}

/** UX-forms: a titled block of the card (section 5.2). */
export interface GroupMeta {
  key: string;
  label: string;
  help?: string | null;
  /** Opens itself when a field inside has a value or an error («Допуски количества»). */
  collapsed?: boolean;
  /** Shown read-only as «Данные источника»; its inputs live under «Дополнительно». */
  source?: boolean;
}

export interface ActionMeta {
  code: string;
  label: string;
  /** print: the code is a print-form path (print-forms/<form>), opened as HTML in a new tab. */
  /** labels: the labels dialog — options from `fields`, a PDF preview of `preview` (the label target), download. */
  kind: "simple" | "lines" | "form" | "print" | "labels";
  confirm: boolean;
  danger: boolean;
  statuses?: string[] | null;
  lines_entity?: string | null;
  lines_fk?: string | null;
  /** kind = "form": inputs of the modal; default_from copies a value from the current row. */
  fields?: ActionField[] | null;
  /** Permission needed for the button; defaults to <entity permission>.write. */
  permission?: string | null;
  /** kind = "lines": whether the modal asks for a location. */
  needs_location?: boolean;
  /** kind = "form": GET /{entity}/{id}/{preview} is shown above the inputs (plan/fact before accepting a shortage). */
  preview?: string | null;
  /** Show only when row[field] === value (kit actions for kits only). */
  /** Shown only when row[field] === value, or — with `present` — when the field is filled (or empty). */
  when?: { field: string; value?: unknown; present?: boolean } | null;
  /** BUG-Z-003: what really happens on confirmation («остатки изменятся» or «…только после утверждения»). */
  outcome?: string | null;
  /** UI-20/21: plain-words description of the button, its step group and its place in the stable order. */
  help?: string | null;
  group?: string | null;
  order?: number;
  /** UXW-05 (31.10.2026): the short words of the button in the grouped panel («Принять»); `label` stays the full name. */
  short_label?: string | null;
}

/** Order of the action groups in a card: preparation → goods → confirmation → execution → after → finish → print. */
export const ACTION_GROUPS: Record<string, { order: number; label: string }> = {
  prepare: { order: 10, label: "Подготовка" },
  goods: { order: 20, label: "Товары" },
  confirm: { order: 30, label: "Подтверждение" },
  execute: { order: 40, label: "Выполнение" },
  after: { order: 50, label: "Дальнейшие операции" },
  finish: { order: 60, label: "Завершение" },
  print: { order: 70, label: "Печать и этикетки" },
  assist: { order: 80, label: "Помощь" },
};

/** GET /{entity}/{id}/workflow — stages, availability of every action and the one recommended next step (section 12). */
export interface WorkflowStage {
  key: string;
  label: string;
  /** ТЗ «Понятные шаги» UX1-006 (3): «waiting» — the stage belongs to a role with another right (hint names it). */
  state: "done" | "current" | "future" | "skipped" | "waiting";
  hint?: string | null;
  /** UX1-008 / section 20: what the stage is made of on the card — the header form, a child tab, action codes. */
  tab?: string | null;
  actions?: string[];
  form?: boolean;
}
export interface WorkflowAction {
  state: "available" | "disabled" | "forbidden" | "done" | "hidden";
  reason?: string | null;
  recommended: boolean;
  /** UX1-042: the button names its subject («Утвердить 7 товаров»); the code and the endpoint do not change. */
  label?: string | null;
}
export interface WorkflowRecommended {
  kind: "action" | "tab" | "wait" | "done";
  code?: string;
  key?: string;
  label: string;
  instruction: string;
}
/** CW31-B02 / UXW-47: the sums of one unit in `Workflow.summary.by_unit` — {"unit": "кг", "quantity_plan": "2.5", …}. */
export interface WorkflowUnitTotal {
  unit: string;
  [field: string]: string;
}
export interface Workflow {
  status: string;
  stages: WorkflowStage[];
  actions: Record<string, WorkflowAction>;
  recommended: WorkflowRecommended | null;
  /** Counters and totals; `by_unit` / `mixed_units` — the sums per unit (the scalar `*_total` mean one unit only). */
  summary: Record<string, string | number | boolean | WorkflowUnitTotal[] | null>;
}

/** Words for the keys of `Workflow.summary` as the stepwise card shows them (ТЗ «Понятные шаги», UX1-006). */
export const WORKFLOW_SUMMARY_LABELS: Record<string, string> = {
  lines: "Строк",
  plan_total: "План",
  fact_total: "Принято",
  picked_total: "Отобрано",
  shipped_total: "Отгружено",
  short_lines: "Строк с недобором",
  cargo_units: "Грузовых мест",
  cargo_units_open: "Открытых мест",
  tasks_open: "Заданий в работе",
  tasks_done: "Заданий выполнено",
  counted: "Посчитано строк",
  uncounted: "Без факта",
  differences: "Расхождений",
  total: "Всего",
};

/** Result of a receive / ship / stage action: what was posted and what to do next (section 6 of the test report). */
/** Status words of a cargo unit (place of shipment): the result of packing names them, never the SSCC (BUG-011). */
export const CARGO_UNIT_STATUSES: Record<string, string> = {
  open: "Открыто",
  closed: "Закрыто",
  partly_shipped: "Частично отгружено", // CW31: the place left with a partly shipped order
  shipped: "Отгружено",
};

export interface ActionResult {
  status: string;
  /** FULL0211 (03.11.2026): the words of `status` when the general dictionary does not know it (брак, списание). */
  status_label?: string;
  /** «Сбросить пароль» of a cabinet login: the new temporary password, shown to the staff once (client flow 30.10). */
  temporary_password?: string;
  username?: string;
  note?: string;
  /** Which object `status` belongs to; «cargo_unit» — the packed place, not the order (BUG-011). */
  status_of?: string;
  /** Number of the cargo unit (SSCC) created by packing, shown under its own label. */
  sscc?: string | null;
  cargo_unit_status?: string;
  order_status?: string;
  /** Planning: tasks created, their numbers and what could not be planned, with the reason (B10). */
  created?: number;
  /** What `created` counts when it is not tasks («Создано строк пересчёта»), N09. */
  created_label?: string;
  /** Internal codes made by «Создать наш штрихкод»; `repeated` — the same request key was answered before. */
  codes?: string[];
  repeated?: boolean;
  numbers?: string[];
  short?: { line_no?: number; short?: string; reason?: string; storage_object_id?: string }[];
  movements?: number;
  details?: {
    line_no: number;
    object?: string;
    location?: string;
    quantity: string;
    /** QA1101-B02: the unit of the product («шт», «кг») — every number of the result is said with it. */
    unit?: string;
    fact?: string;
    plan?: string;
    /** BUG-R31-012: the batch a receipt line went into. */
    batch?: string | null;
    allocations?: { batch: string; quantity: string }[];
  }[];
  cancelled_tasks?: string[];
  next_step?: string | null;
  /** UX1-044: the record an action created («Пересчитать оставшиеся» → the new draft), opened from the result. */
  open_path?: string | null;
  document_id?: string;
  /** Approval of a count: how many lines were posted and how many stayed without a fact (UX1-042). */
  approved_lines?: number;
  uncounted_lines?: number;
  moved?: { task: string; from: string; quantity: string }[];
  skipped?: { task: string; reason: string }[];
  jobs?: number;
  labels?: number;
}

/** GET /topology/zones/summary */
export interface ZoneSummaryRow {
  zone_id: string | null;
  code: string;
  name: string;
  kind: string | null;
  total: number;
  free: number;
  occupied: number;
  reserved: number;
  blocked: number;
}

/** A line of the plan/fact table above a form action (D08: «Принять с расхождениями»). */
export interface ActionPreviewLine {
  line_no: number;
  storage_object_code: string;
  storage_object_name: string;
  plan: string;
  fact: string;
  diff: string;
}

/**
 * GET /{entity}/{id}/{ActionMeta.preview} of a form action: the plan/fact lines (D08) or — «Завершить» of a task,
 * QA0211-B02 — what the dialog is about (task, product, batch, plan in its unit, cells), what the action does, and
 * the unit of a quantity field with its digits after the comma (the precision the server checks before posting).
 */
export interface ActionFormPreview {
  lines?: ActionPreviewLine[];
  context?: { label: string; value: string }[];
  help?: string | null;
  units?: Record<string, { name: string; decimals: number; product?: string }>;
  /** The unit is not known before the task is done (a count of a cell): the server checks it on «Завершить». */
  unit_note?: string | null;
}

export interface ActionField {
  name: string;
  label: string;
  type: "string" | "number" | "ref" | "enum" | "boolean" | "date";
  ref?: string;
  options?: { value: string; label: string }[];
  required?: boolean;
  default_from?: string;
  /** Value the dialog starts with (the label format of the card). */
  default?: unknown;
}

/** POST /labels/preview: what the PDF will hold before it is downloaded or printed. */
export interface LabelsPreview {
  title: string;
  count: number;
  total: number;
  pages: number;
  format: string;
  format_label: string;
  problems: string[];
  missing_barcodes: number;
  items: { title: string; code: string | null }[];
}

/** «ИИ Помощник» (ТЗ 29.10.2026): GET /assistant/capabilities — what this employee and client may do. */
export interface AssistantCapabilities {
  enabled: boolean;
  connected: boolean;
  provider: string | null;
  model: string | null;
  /** Shown instead of the dialog while the server has no model key: the WMS works as before. */
  message: string | null;
  documents: boolean;
  actions: boolean;
  operations: boolean;
  can_receipt: boolean;
  can_order: boolean;
  voice: "device";
  limits: { file_mb: number; pages: number };
}

export interface AssistantPreviewLine {
  line_no: number;
  code: string;
  name: string;
  unit: string;
  quantity: string;
  /** «3 × Короб (50 шт) = 150 шт» — computed by the server. */
  text: string;
  available?: string;
}

export interface AssistantPreview {
  warehouse?: string | null;
  partner?: { id: string; code: string; name: string; bin: string | null; roles: string[] } | null;
  supplier_number?: string | null;
  supplier_date?: string | null;
  lines?: AssistantPreviewLine[];
  totals?: { unit: string; quantity: string }[];
  /** Operations on a document: its number, status and the card. */
  document?: string;
  document_path?: string;
  status?: string;
  actor?: string;
  effect: string;
  warnings: string[];
}

export interface AssistantActionResult {
  text: string;
  number?: string;
  status?: string;
  status_label?: string;
  document_id?: string;
  document_type?: "expected_receipt" | "shipment_order";
  path?: string;
}

/** A proposal of the assistant: nothing happens until the employee presses `button` (R1 draft / R2 operation). */
export interface AssistantAction {
  id: string;
  intent: string;
  risk: "R1" | "R2";
  title: string;
  button: string;
  status: "awaiting_confirmation" | "executing" | "succeeded" | "failed" | "cancelled" | "stale";
  status_label: string;
  preview: AssistantPreview;
  result: AssistantActionResult | null;
  error: string | null;
  expires_at: string | null;
  can_execute: boolean;
}

export interface AssistantMessage {
  id: string;
  seq: number;
  role: "user" | "assistant";
  /** text · question (with options) · proposal (with action) · result · error · source · extraction */
  kind: "text" | "question" | "proposal" | "result" | "error" | "source" | "extraction";
  text: string | null;
  channel: "text" | "voice" | "photo";
  created_at: string | null;
  options?: string[] | null;
  code?: string | null;
  /** proposal / result: the action they belong to; result: the card of the created document. */
  action_id?: string | null;
  path?: string | null;
  action?: AssistantAction | null;
  extraction?: { id: string; status: string; direction: string; lines: number; blocking: string[]; document: { type: string; id: string; path: string } | null; error: string | null } | null;
}

export interface AssistantConversation {
  id: string;
  client: "web" | "tsd";
  title: string | null;
  warehouse: { id: string; code: string; name: string } | null;
  created_at: string | null;
  last_message_at: string | null;
  messages?: AssistantMessage[];
}

export interface AssistantCandidate {
  id: string;
  code: string;
  name: string;
  article: string | null;
  gtin: string | null;
  unit: string;
  fractional: boolean;
  packages: { id: string; name: string; ratio: string; base: boolean }[];
  barcodes: string[];
  storage: string | null;
}

/** A recognised invoice line: what was printed (`source`, `page`), the chosen product and the server's check. */
export interface AssistantExtractionLine {
  index: number;
  source: Record<string, string | null>;
  page: number | null;
  recognition_issues: string[];
  candidates: AssistantCandidate[];
  match: "code" | "article" | "name" | null;
  storage_object_id: string | null;
  package_id: string | null;
  packages_count: string | null;
  quantity: string | null;
  quantity_base?: string | null;
  quantity_text?: string;
  unit?: string;
  state: "recognized" | "confirmed" | "edited" | "skipped";
  /** Распознано · Нужна проверка · Подтверждено/Исправлено · Пропущено */
  status: "recognized" | "needs_review" | "confirmed" | "skipped";
  issues: string[];
}

export interface AssistantParty {
  name: string | null;
  /** A local БИН / ИИН only (KZ 30.10.2026); a foreign number of the original is in `tax_id`. */
  bin: string | null;
  raw: string | null;
  partner_id: string | null;
  candidates: { id: string; code: string; name: string; bin: string | null; kind?: string | null; tax_id_label?: string; roles: string[] }[];
  /** ТЗ реквизитов §11.2: the identifier with its meaning — «БИН», «ИИН», «БИН/ИИН» or «Идентификатор в оригинале». */
  tax_id?: AssistantTaxId | null;
  /** A foreign side: never proposed as a local card; its data go into the international attachment. */
  foreign?: boolean;
  hint?: string | null;
}

export interface AssistantTaxId {
  value: string | null;
  kind: "bin" | "iin" | "local" | "foreign" | "original" | null;
  label: string | null;
  country: string | null;
  local: boolean;
  foreign?: boolean;
  checksum_ok?: boolean;
}

/** GET /admin/kz-data-check — «Проверка данных под Казахстан» (ТЗ реквизитов §15.1), read-only; identifiers masked. */
export interface KzCardRef {
  id: string;
  name: string;
  path: string;
  form?: string | null;
  tax_id?: string | null;
}

export interface KzDataCheck {
  generated_at: string;
  limit: number;
  partners: {
    total: number;
    forms: { kind: string | null; label: string; legacy: boolean; count: number }[];
    legacy_fields: { inn: number; kpp: number; items: (KzCardRef & { inn: string | null; kpp: string | null })[] };
    tax_id_problems: {
      missing: number;
      format: number;
      checksum: number;
      items: (KzCardRef & { problem: "missing" | "format" | "checksum"; text: string; roles: string[] })[];
    };
    duplicates: { catalogue: string; catalogue_label: string; tax_id: string; cards: KzCardRef[] }[];
  };
  companies: (KzCardRef & { missing: string[]; invalid: string[] })[];
  warehouses: { id: string; code: string; name: string; path: string; timezone: string; timezone_label: string; receipts: number; orders: number; stock_rows: number }[];
  default_timezone: string;
  default_timezone_label: string;
  serial_kinds: { kind: string; label: string; count: number }[];
  documents_of_legacy_partners: { table: string; entity: string | null; label: string; field: string; count: number; examples: { id: string; number: string; path: string | null }[] }[];
}

export interface AssistantExtraction {
  id: string;
  revision: number;
  status: "needs_review" | "ready" | "created" | "failed";
  direction: "receipt" | "shipment" | "transfer" | "unknown" | "unsupported";
  button: string;
  source_id: string;
  source_url: string;
  document: { type: string; id: string; path: string } | null;
  error: string | null;
  doc_type: string;
  doc_type_label: string;
  title: string | null;
  number: { raw: string | null; page: number | null; value: string | null };
  date: { raw: string | null; page: number | null; value: string | null; problem?: string | null };
  supplier: AssistantParty;
  recipient: AssistantParty;
  partner_id: string | null;
  lines: AssistantExtractionLine[];
  totals: { unit: string; quantity: string }[];
  totals_printed: { quantity: string | null; amount: string | null; currency: string | null };
  pages_seen: number | null;
  readability: "good" | "partial" | "poor" | null;
  problems: string[];
  /** Why «Создать …» is not available yet; empty — the document can be created. */
  blocking: string[];
}

export type TaskType = "placement" | "pick" | "move" | "replenish" | "count" | "move_mx" | "ship" | "control";

export const TASK_TYPES: Record<TaskType, string> = {
  placement: "Размещение",
  pick: "Отбор",
  move: "Перемещение",
  replenish: "Пополнение",
  count: "Пересчёт",
  move_mx: "Перемещение МХ",
  ship: "Отгрузка",
  control: "Контроль",
};

/** GET /tasks/board */
export interface TaskBoard {
  warehouse_id: string | null;
  generated_at: string;
  queues: { queue_id: string | null; queue_code: string; queue_name: string; counts: Record<string, number>; overdue: number; stuck: number }[];
  executors: {
    user_id: string;
    username: string;
    full_name: string;
    current_task: string | null;
    current_status: string | null;
    done_today: number;
  }[];
  problems: number;
  connections: number;
}

/** Message of the /ws/tasks channel. */
export interface TaskSocketMessage {
  channel: string;
  type: string;
  action?: string;
  task?: { id: string; number: string; status: TaskStatus; type: TaskType; assignee_id: string | null };
}

export interface EntityMeta {
  name: string;
  label: string;
  label_plural: string;
  section: string;
  section_label: string;
  permission: string;
  searchable: string[];
  /** BUG-Z-002: what the search box looks through; with no searchable fields and no hint the list has no search. */
  search_hint?: string | null;
  default_sort: string;
  readonly: boolean;
  fields: FieldMeta[];
  children: ChildMeta[];
  actions: ActionMeta[];
  menu: boolean;
  /** «Создать поступление» instead of «Новый: Ожидаемое поступление», and what happens after saving (UX-06). */
  create_title?: string | null;
  create_hint?: string | null;
  /** Default list columns when the user has not chosen their own. */
  list_fields?: string[];
  /** BUG-R40-003: in these statuses the card is read-only for a plain save; the hint names the action to use. */
  frozen_statuses?: string[];
  frozen_hint?: string | null;
  /** CW31-B08: per status, the fields a plain save may change; the others are shown read-only with the way to fix them. */
  edit_policy?: EditPolicy | null;
  /** False: the list offers no «Создать» — rows come from another card (the gates of a dock). */
  creatable?: boolean;
  /** The card shows POST /{entity}/form-preview above «Сохранить» while the form is filled. */
  form_preview?: boolean;
  /** UX-forms (ТЗ 29.10.2026): blocks of the card, «+» in reference lists, pick labels, ordinary lookup, workflow. */
  groups?: GroupMeta[];
  inline_create?: boolean;
  pick_label?: PickLabel | null;
  lookup?: boolean;
  workflow?: boolean;
  /** KZ-07: the placeholder of a pick list of this catalogue — only what its search really looks through. */
  pick_hint?: string | null;
  /** CW31: a boolean column whose «нет» lists the row under «Архив» in pick lists (contracts: «Действует»). */
  archive_field?: string | null;
}

/** How a row of a catalogue is named in pick lists: «Name · БИН/ИИН 123…» (section 8.1). */
export interface PickLabel {
  primary: string[];
  /** KZ 30.10.2026: a word may follow another field of the row — {field: "bin", word: "БИН/ИИН", by: "kind", words: {too: "БИН", ie: "ИИН"}}. */
  secondary?: (string | [string, string] | { field: string; word?: string; by?: string; words?: Record<string, string> })[];
  missing?: string;
  nameless?: string;
}

/** Static «(?)» texts of the tabs every card has (UI-19). */
export const TAB_HELP: Record<string, string> = {
  main: "Реквизиты, статус документа и текущий шаг работы.",
  files: "Прикреплённые документы, фото и другие разрешённые вложения.",
  history: "Кто, когда и что изменял в записи — в пределах доступного журнала.",
  // child tabs registered by modules that give no text of their own, by the entity of the tab
  tasks: "Связанные складские задания и их выполнение.",
  stock: "Остатки по этой записи: где и сколько лежит.",
  task_events: "История задания: статусы, исполнители и время.",
  task_scan_events: "Сканы ТСД по этому заданию: что и когда отсканировано.",
};

/** One row of GET /stock/summary. */
export interface StockSummaryRow {
  storage_object_id: string;
  storage_object_code: string;
  storage_object_name: string;
  quantity: string;
  planned_in: string;
  planned_out: string;
  /** quantity − planned_out, whatever the state or the expiry (BUG-Z-009: «Не зарезервировано»). */
  unreserved: string;
  /** Free stock an ordinary pick may take: usable state, batch not expired («Доступно к отбору»). */
  available: string;
  /** Free stock the pick planner never offers, with its reasons. */
  excluded: string;
  excluded_reasons: string[];
  locations: number;
  /** UXW-35/38 (31.10.2026): the article (searched too) and the unit of every quantity with its precision. */
  storage_object_article?: string | null;
  unit?: string | null;
  unit_code?: string | null;
  unit_decimals?: number;
}

/** UXW-37: GET /stock/summary/scope — the warehouses of the user and the owners of the stock facts in them. */
export interface StockSummaryScope {
  warehouses: { id: string; code?: string | null; name: string }[];
  owners: { id: string; code?: string | null; name: string }[];
  /** There are rows whose owner is «принадлежность требует сверки» (ADR 0014). */
  unknown_owner: boolean;
}

/** UXW-39…46: GET /shipment_orders/{id}/available-items — the owner's stock on the warehouse of the order. */
export interface PickerPackage {
  id: string;
  code: string;
  name: string;
  /** Base units in one package: "50" for a box of 50 pieces. */
  ratio: string;
  is_base?: boolean;
  barcode?: string | null;
}

export interface PickerItem {
  storage_object: { id: string; code: string; article?: string | null; gtin?: string | null; name: string };
  /** A card of the common catalog (no owner): the stock shown is still only this owner's. */
  common: boolean;
  unit: { code?: string | null; label: string; precision: number };
  /** Decimal strings, never floats. */
  quantity: string;
  reserved: string;
  available: string;
  excluded: string;
  excluded_reasons: string[];
  packages: PickerPackage[];
  /** The plan of this product in the lines of this order already. */
  in_order: string;
  /** PS10: what this order itself has reserved. */
  reserved_by_order: string;
}

export interface AvailableItems {
  order: { id: string; number: string };
  warehouse: { id: string; code?: string | null; name: string };
  owner: { id: string; code?: string | null; name: string };
  generated_at: string;
  total: number;
  items: PickerItem[];
}

/** Result of GET /barcodes/resolve. */
export interface BarcodeResolve {
  value: string;
  matched: "barcode" | "gtin" | "structure" | null;
  storage_object_id: string | null;
  storage_object_code: string | null;
  storage_object_name: string | null;
  package_id: string | null;
  package_code: string | null;
  batch_id: string | null;
  batch_code: string | null;
  quantity: string | null;
  structure_code: string | null;
  fields: Record<string, unknown>;
}

/** Result of GET /scan: one recognised object, a choice, or a reason (task of 22.10, section 4). */
export interface ScanCandidate {
  kind: string;
  entity: string;
  id: string;
  label: string;
  path: string;
  hint?: string | null;
}
export interface ScanResult {
  raw: string;
  value: string;
  format: "plain" | "gs1";
  kind: "product" | "package" | "group" | "serial" | "aggregation" | "cargo_unit" | "location" | "document" | "task" | "batch" | "packing_list" | "ambiguous" | "invalid" | "forbidden" | "unknown";
  entity?: string | null;
  id?: string | null;
  label?: string | null;
  path?: string | null;
  hint?: string | null;
  reason?: string | null;
  status?: string | null;
  storage_object_id?: string | null;
  storage_object_code?: string | null;
  package_id?: string | null;
  package_ratio?: string | null;
  quantity?: string | null;
  location_code?: string | null;
  container: boolean;
  parents: { id: string; code: string; type: string; container: string }[];
  fields: Record<string, unknown>;
  candidates: ScanCandidate[];
  actions: string[];
}

/** Result of POST /import/{entity}. */
export interface ImportReport {
  total: number;
  created: number;
  updated: number;
  failed: number;
  dry_run: boolean;
  errors: { row: number; message: string }[];
  /** KZ 30.10.2026: accepted but not taken from the file (the ИНН / КПП columns of an old template); row 1 — the file. */
  warnings?: { row: number; message: string }[];
}

export interface EntityListItem {
  name: string;
  label: string;
  label_plural: string;
  section: string;
  section_label: string;
  permission: string;
  readonly: boolean;
  menu: boolean;
  /** Feature option that must be on for the entity to be listed. */
  option?: string | null;
}

/** Server-driven TSD screens: the app renders exactly these seven primitives. */
export type TsdCommand =
  | "confirm"
  | "cancel"
  | "problem"
  | "skip"
  | "info"
  | "print"
  | "take_all"
  | "finish"
  | "exit"
  | "create_mx"
  | "close"
  | "edit_qty"
  | "edit_place"
  | "empty";

export interface TsdCommandSpec {
  code: TsdCommand;
  label: string;
  /** Physical key mapped by the device model, e.g. "F1". */
  key?: string;
}

/** Persistent header of every screen (ТЗ «ТСД на смартфоне», UX-001): where the worker is and what they do. */
export interface TsdContext {
  warehouse?: string | null;
  operation?: string | null;
  document?: string | null;
  user?: string | null;
  /** receive | placement | pick | pack | control | ship | move | count | writeoff | info — colour and icon. */
  kind?: TsdOperationKind | null;
  /** «Понятные операции плюс» (31.10.2026): the route of the work — «RECV-01 → A-01-02» on the «В работе» card. */
  source?: string | null;
  target?: string | null;
}

export type TsdOperationKind = "receive" | "placement" | "pick" | "pack" | "control" | "ship" | "move" | "count" | "writeoff" | "info";

/** «Понятные операции» (UX/UI 26.10.2026): one «label — value» line of a card. */
export interface TsdRow {
  label: string;
  value: string;
  tone?: "normal" | "success" | "warning" | "danger";
}

/** What the step is about: a product, a pallet or box, a cell — the card under the title. */
export interface TsdSubject {
  kind: "product" | "container" | "cell" | "document";
  name: string;
  details?: string | null;
}

/** One part of the step bar «✓ Товар · 2. Количество · 3. Ячейка · 4. Проверка» («Понятные операции плюс»). */
export interface TsdStepMark {
  label: string;
  state: "done" | "current" | "todo";
}

/** The quantity of a confirmation: «Будет принято 50 шт», «1 × 50 шт + 0 шт». */
export interface TsdAmount {
  label: string;
  value: string;
  unit: string;
  formula?: string | null;
}

interface TsdScreenBase {
  /** Session step identifier, echoed back with the input. */
  step: string;
  title: string;
  /** Progress text like "3 из 12". */
  progress?: string;
  hint?: string;
  /** «Что сканировать» / what happens next, in plain words (UX-011). */
  help?: string | null;
  context?: TsdContext | null;
  commands: TsdCommandSpec[];
  subject?: TsdSubject | null;
  rows?: TsdRow[];
  /** The named stages behind `progress`; absent or empty when the work has no fixed stages. */
  steps?: TsdStepMark[];
}

export interface TsdScanLocation extends TsdScreenBase {
  type: "scan_location";
  expected?: string;
  /** "any" accepts any location, "expected" requires the expected one. */
  mode: "any" | "expected";
  /** What the step wants: a cell, a pallet / box, or either (SC-06). */
  expect?: "cell" | "container" | "any";
  /** «Зона → Ряд → Стеллаж → Ярус → Ячейка» of the expected address (FR-034). */
  description?: string | null;
}

export interface TsdScanItem extends TsdScreenBase {
  type: "scan_item";
  expected?: { code: string; name: string; imageUrl?: string };
  expect?: "product" | "any";
}

export interface TsdEnterQty extends TsdScreenBase {
  type: "enter_qty";
  item: { code: string; name: string };
  unit: string;
  planned?: number;
  allowExcess?: boolean;
  /** The package the worker scanned: the app shows «5 упаковок × 10 шт = 50 шт» and sends base units (FR-023). */
  package?: { name: string; ratio: string } | null;
  /** Fact already posted for this line / task and what is still planned. */
  done?: number | null;
  remaining?: number | null;
  /** BUG-R31-010: digits after the comma the unit allows — 0 for pieces, 3 for kilograms. */
  decimals?: number;
  /** BUG-R31-007: a count records «0» — the place really is empty. */
  allowZero?: boolean;
  /** «Далее: выбрать место». */
  nextLabel?: string | null;
}

export interface TsdEnterText extends TsdScreenBase {
  type: "enter_text";
  placeholder?: string;
  keyboard: "default" | "numeric" | "date";
  /** Suggested value shown in the field (expiry date from shelf life, previous batch). */
  value?: string | null;
  /** The value is a code (serial number): the terminal offers the camera and the hardware scanner. */
  scan?: boolean;
}

export interface TsdChoose extends TsdScreenBase {
  type: "choose";
  options: { value: string; label: string; description?: string }[];
  /** The list also accepts a scanned or typed code (receipt number, product barcode), SC-04. */
  scan?: boolean;
}

export interface TsdConfirm extends TsdScreenBase {
  type: "confirm";
  lines: string[];
  quantity?: TsdAmount | null;
  /** What the button does to the stock: «Остаток изменится после подтверждения». */
  effect?: string | null;
  /** «План 100 шт · Уже принято 50 шт · После приёмки 100 шт». */
  stats?: TsdRow[];
  /** «Изменить количество», «Изменить место». */
  edits?: TsdCommandSpec[];
}

export interface TsdMessage extends TsdScreenBase {
  type: "message";
  level: "info" | "success" | "error";
  text: string;
  /** SC-27: exact facts of the posted operation (quantity, address, totals). */
  facts?: string[];
  /** «Приёмка этого количества завершена. Возврат к заданиям её не отменяет.» */
  note?: string | null;
  /** BUG-R40-001: `text` only repeats `rows` for clients without rows — a client that draws the rows skips it. */
  text_in_rows?: boolean;
}

export type TsdScreen = TsdScanLocation | TsdScanItem | TsdEnterQty | TsdEnterText | TsdChoose | TsdConfirm | TsdMessage;

export interface TsdStepInput {
  step: string;
  value?: string | number;
  command?: TsdCommand;
  /** One aim of the camera is one event; a retry of a lost answer repeats the same id (FR-012, FR-064). */
  event_id?: string;
  source?: "camera" | "scanner" | "manual" | "button";
}

/** Row of GET /tsd/queues. */
export interface TsdQueue {
  id: string;
  code?: string;
  name: string;
  tasks: number;
  /** Task types of the queue: the home tiles find «Собрать заказ» (pick) and «Отгрузить товар» (ship) by them. */
  types?: string[];
  icon?: string;
  /** "receiving" starts the by-document receiving dialog, "free" the menu of free operations. */
  mode?: "tasks" | "receiving" | "free";
}

/** Result of POST /tsd/sessions. */
export interface TsdSessionStart {
  session_id: string;
  screen: TsdScreen;
}

/** Node of GET /topology/tree. */
export interface TopologyNode {
  key: string;
  kind: "warehouse" | "room" | "aisle" | "rack";
  id: string;
  code: string;
  name: string;
  locations: number;
  children: TopologyNode[];
}

/** Body of POST /locations/generate. */
export interface GenerateLocationsRequest {
  rack_ids: string[];
  type_id: string;
  zone_id?: string | null;
  state_id?: string | null;
  level_from: number;
  level_to: number;
  position_from: number;
  position_to: number;
  template: string;
  barcode_template?: string | null;
  serpentine: boolean;
  dry_run: boolean;
}

export interface GenerateLocationsPreview {
  total: number;
  created: number;
  skipped: number;
  dry_run: boolean;
  sample: string[];
  skipped_codes: string[];
}

/** Static pages of the ARM shown in the menu next to generated entity lists. */
export const STATIC_PAGES: { section: string; path: string; label: string; permission: string }[] = [
  { section: "topology", path: "/topology", label: "Схема склада", permission: "topology.locations.read" },
  { section: "inventory", path: "/stock-summary", label: "Остатки по товарам", permission: "inventory.stock.read" },
  { section: "resources", path: "/dispatcher", label: "Диспетчер", permission: "tasks.dispatch" },
  { section: "inbound", path: "/dock-schedule", label: "Расписание доков", permission: "inbound.dock_slots.read" },
  { section: "analytics", path: "/reports", label: "Отчёты", permission: "analytics.reports.read" },
  { section: "admin", path: "/settings", label: "Настройки системы", permission: "core.settings.read" },
  // KZ-DOC TMP01: the catalogue of document layouts next to «Шаблоны этикеток», never instead of it
  { section: "admin", path: "/document-templates", label: "Шаблоны документов", permission: "document_templates.read" },
];


/** Report registry (GET /reports) and result (GET /reports/{code}). */
export interface ReportParam {
  name: string;
  label: string;
  /** "datetime" — a day or an instant («срез 04.11 10:00»), ТЗ 1.2 раздел 10. */
  type: "date" | "datetime" | "ref" | "enum" | "number" | "string" | "boolean";
  ref?: string | null;
  required: boolean;
  default?: unknown;
  /** Lower bound of a number parameter (days ≥ 1), QA20-005. */
  min?: number | null;
  options?: { value: string; label: string }[] | null;
  /** One-line hint under the field (ТЗ 1.2 раздел 9.1). */
  description?: string | null;
}

export interface ReportColumn {
  name: string;
  label: string;
  type: "string" | "number" | "datetime" | "date" | "boolean" | "percent";
}

export interface ReportMeta {
  code: string;
  name: string;
  description: string;
  group: string;
  permission: string;
  params: ReportParam[];
  columns: ReportColumn[];
  summary: string[];
  /** ТЗ 1.2 раздел 9.4 «Показать подробнее»: GET /reports/{code}/details?params&key with a row's `_key`. */
  drill?: boolean;
  drill_columns?: ReportColumn[];
  /** CW31-B02: the column with the unit of a row and the summed columns measured in it (footer per unit). */
  unit_column?: string | null;
  unit_summary?: string[];
}

export interface ReportResult {
  code: string;
  /** The actual columns of this run (configurable age ranges change them), else the registry's. */
  columns: ReportColumn[];
  rows: Record<string, unknown>[];
  total: number;
  /** CW31-B02: null for a column of `unit_summary` when the rows have several units — see `totals_by_unit`. */
  totals: Record<string, string | number | null>;
  /** ТЗ 1.2 раздел 9.4: «Данные на …» — as_of, watermark, timezone / day_boundary, complete, counters, note. */
  meta?: Record<string, unknown>;
  /** CW31-B02: the footer per unit — never one sum of pieces and kilograms. */
  totals_by_unit?: { unit: string; totals: Record<string, string | number> }[];
  /** FULL0211-B07: the same footer in words per column of `unit_summary` — `{ quantity: "110 шт; 3 кг" }` (PDF, Excel). */
  totals_text?: Record<string, string>;
}

/** Saved view of a report (ТЗ 1.2 раздел 9.1, 16.1 ReportView): parameters only, rights are checked on each run. */
export interface ReportView {
  id: string;
  report_code: string;
  name: string;
  params: Record<string, unknown>;
  group_by: string | null;
  owner: string;
  is_shared: boolean;
  mine: boolean;
  can_delete: boolean;
  created_at: string;
  updated_at: string;
}

// ---- KZ-DOC 30.10.2026: document renditions (one stored PDF for the preview, the download and the print) ----------

/** UI04: a requisite the chosen layout needs and the document does not have — what, where to correct, who may. */
export interface RenditionMissing {
  field: string;
  label: string;
  /** Staff only: the cabinet of a client gets what is missing, never where it is corrected (TMP21). */
  fix?: string | null;
  route?: string | null;
  can_fix?: boolean;
}

/** A stored PDF of a document: `GET /document-renditions/{id}`, the `latest` of a layout, a line of the history. */
export interface Rendition {
  id: string;
  doc_kind: string;
  document_id: string;
  document_number: string | null;
  template_id: string;
  template_name: string | null;
  template_version: number;
  created_at: string;
  /** Absent in the cabinet of a client. */
  created_by?: string | null;
  pages: number;
  size: number;
  pdf_sha256: string;
  snapshot_sha256: string;
  filename: string;
  organization: string | null;
  /** TMP18: false — the data or the layout version changed since the file was formed; null — not checked. */
  current: boolean | null;
  supersedes_id: string | null;
}

/** One layout the document may be printed with and the file it last gave. */
export interface RenditionTemplateOption {
  template_id: string;
  /** code, purpose, legacy_form, accounting_review — staff only; the cabinet gets no administrative fields (TMP21). */
  code?: string;
  name: string;
  purpose?: string | null;
  legacy_form?: string | null;
  /** The published version valid on the date of the document; null — no applicable version. */
  version: number | null;
  accounting_review?: "not_required" | "pending" | "confirmed" | null;
  missing: RenditionMissing[];
  latest: Rendition | null;
}

/** GET /documents/{kind}/{id}/renditions/available */
export interface RenditionsAvailable {
  kind: string;
  kind_label: string;
  document_id: string;
  number: string | null;
  status: string | null;
  cancelled: boolean;
  organization: string | null;
  templates: RenditionTemplateOption[];
}

/** UI06: formed / given for download / print dialog opened — never «напечатано» (TMP23). */
export interface RenditionEvent {
  at: string;
  actor: string | null;
  action: "render" | "download" | "print_dialog" | "view" | string;
  label: string;
  rendition_id: string;
  template_version: number | null;
}

/** GET /documents/{kind}/{id}/renditions */
export interface RenditionHistory {
  renditions: Rendition[];
  events: RenditionEvent[];
}

/** A field of the document kind a layout may print (GET /document-sources). */
export interface DocFieldSpec {
  path: string;
  label: string;
  type: "text" | "qty" | "money" | "date" | "datetime" | "int" | "bool" | string;
}

export interface DocumentSource {
  kind: string;
  label: string;
  fields: DocFieldSpec[];
  tables: Record<string, DocFieldSpec[]>;
  /** The kind has «учебные данные» for the preview of a layout. */
  sample: boolean;
}

export interface DocumentSources {
  sources: DocumentSource[];
  blocks: { type: LayoutBlockType; label: string }[];
  purposes: { value: string; label: string }[];
  statuses: { value: string; label: string }[];
  accounting_review: { value: string; label: string }[];
}

/** The declarative layout of a template (server `printing/layout.py`): allowed blocks only, no code, no markup. */
export type LayoutFormat = "text" | "qty" | "money" | "date" | "datetime" | "int" | "bool";
export type LayoutBlockType = "title" | "text" | "fields" | "parties" | "table" | "totals" | "signatures" | "barcode" | "spacer" | "page_break";

export interface LayoutFieldRow {
  label: string;
  field: string;
  format?: LayoutFormat | null;
  bold?: boolean;
}

export interface LayoutTableColumn {
  title: string;
  field: string;
  width?: number;
  align?: "left" | "right" | "center" | null;
  format?: LayoutFormat | null;
}

export interface LayoutTableTotal {
  column: string;
  field: string;
  format?: LayoutFormat | null;
}

export type LayoutBlock =
  | { type: "title"; text: string; number?: boolean; subtitle?: string | null; align?: "center" | "left" }
  | { type: "text"; text: string; style?: "normal" | "small" | "bold" | "note" }
  | { type: "fields"; title?: string | null; columns?: 1 | 2; rows: LayoutFieldRow[]; hide_empty?: boolean }
  | { type: "parties"; columns: { title: string; rows: LayoutFieldRow[] }[] }
  | {
      type: "table";
      source: string;
      title?: string | null;
      columns: LayoutTableColumn[];
      totals?: LayoutTableTotal[];
      total_label?: string;
      empty_text?: string;
      numbered?: boolean;
    }
  | { type: "totals"; rows: LayoutFieldRow[] }
  | { type: "signatures"; items: { role: string; name_field?: string | null }[]; columns?: 1 | 2 | 3; stamp?: boolean }
  | { type: "barcode"; field: string; kind?: "code128" | "qr"; height_mm?: number }
  | { type: "spacer"; height_mm?: number }
  | { type: "page_break" };

export interface LayoutPage {
  size?: "A4" | "A5";
  orientation?: "portrait" | "landscape";
  /** top, right, bottom, left — millimetres */
  margins_mm?: [number, number, number, number];
  font_size?: number;
  page_numbers?: boolean;
  footer?: string | null;
}

export interface TemplateLayout {
  page?: LayoutPage;
  blocks: LayoutBlock[];
  /** Requisites without which the layout is not issued (TMP08). */
  required?: string[];
}

export type TemplateVersionStatus = "draft" | "review" | "published" | "archived";

export interface DocumentTemplateVersion {
  id: string;
  template_id: string;
  version: number;
  status: TemplateVersionStatus;
  valid_from: string | null;
  valid_to: string | null;
  layout: TemplateLayout;
  accounting_review: "not_required" | "pending" | "confirmed";
  reviewed_by: string | null;
  reviewed_at: string | null;
  checked_at: string | null;
  check_result: LayoutCheck | null;
  published_by: string | null;
  published_at: string | null;
  reason: string | null;
  note: string | null;
  created_at: string;
  created_by: string | null;
  updated_at: string | null;
}

/** GET /document-templates (items) and GET /document-templates/{id} (with `versions`). */
export interface DocumentTemplate {
  id: string;
  code: string;
  name: string;
  doc_kind: string;
  doc_kind_label: string;
  purpose: string;
  company_id: string | null;
  company_name: string | null;
  language: string;
  basis: string | null;
  legacy_form: string | null;
  is_system: boolean;
  is_active: boolean;
  base_template_id: string | null;
  sort_order: number;
  active_version: DocumentTemplateVersion | null;
  latest_version: number | null;
  latest_status: TemplateVersionStatus | null;
  draft_version_id: string | null;
  updated_at: string | null;
  versions?: DocumentTemplateVersion[];
}

/** POST /document-template-versions/{id}/validate: problems in the words of the editor (AT28). */
export interface LayoutCheck {
  ok: boolean;
  problems: string[];
  pages_on_sample: number | null;
}

/** GET /dashboard */
export interface Dashboard {
  generated_at: string;
  warehouse_id: string | null;
  /** «Сегодня» of the counters: the date and the warehouse time zone it is taken in. */
  today?: string;
  timezone?: string;
  receipts: { planned_today: number; in_progress: number; accepted_today: number };
  orders: { planned_today: number; picking: number; shipped_today: number };
  tasks: { ready: number; in_progress: number; problem: number; done_today: number; executors: number };
  stock: { objects: number; locations: number; quantity: string };
  alerts: { kind: string; level: "info" | "warning" | "danger"; text: string; link: string }[];
  recent_events: { type: string; created_at: string; payload: Record<string, unknown> }[];
  reports: number;
}


/**
 * Sidebar of the ARM: a short list of everyday screens. Every other entity is reachable from the hub pages
 * "Справочники" (/catalog) and "Администрирование" (/admin), which list all entities of their sections with search.
 */
export interface NavItem {
  /** "page" = a static route, "entity" = a generated list of the registry. */
  kind: "page" | "entity";
  key: string;
  label: string;
  permission: string;
  /** Shown only when this feature option is enabled. */
  option?: string;
  /** UI-19: short description of the screen, shown by the «(?)» next to the menu item. */
  help?: string;
}

export interface NavSection {
  code: string;
  label: string;
  items: NavItem[];
  help?: string;
}

/** «(?)» texts of the sidebar, by route or entity key (UI-19); the names, order and routes of NAV stay as they are. */
export const NAV_HELP: Record<string, string> = {
  "/dashboard": "Сводка по складу: ожидаемые поступления, заказы, задания и отклонения.",
  "/assistant": "ИИ Помощник: задачи текстом и голосом, накладные по фото; действия выполняются только по кнопке.",
  "/billing/operations": "Завершённые операции клиента за период: отметьте нужные — сумма по тарифу на дату операции, затем реестр услуг и счёт.",
  expected_receipts: "Планы прихода товара: создать поступление, добавить товары, подтвердить и принять.",
  "/dock-schedule": "Окна на воротах (доках) по дням: кто и когда разгружается или грузится.",
  shipment_orders: "Заказы клиентов: создать, добавить товары, подтвердить, собрать и отгрузить.",
  waves: "Объединение заказов в волну для совместного отбора.",
  cargo_units: "Грузовые места (SSCC): упаковка заказов, состав и этикетки.",
  "/work": "Рабочий экран склада: крупные кнопки операций для работы с ПК.",
  "/stock-summary": "Остатки по товарам: где и сколько лежит, что зарезервировано.",
  "/dispatcher": "Диспетчер: все задания и исполнители в реальном времени, назначение и блокировка.",
  tasks: "Складские задания: размещение, отбор, перемещение, пересчёт и их выполнение.",
  stock_counts: "Инвентаризации: подготовка, пересчёт, расхождения и утверждение.",
  stock_movements: "Журнал движений товара: каждое изменение остатка с документом и исполнителем.",
  "/topology": "Схема склада в 2D и 3D: зоны, стеллажи и ячейки с остатками.",
  locations: "Места хранения (ячейки): адреса, типы, состояния и штрихкоды.",
  zones: "Зоны склада: приёмка, хранение, отгрузка и их параметры.",
  docks: "Пандусы и ворота на схеме склада.",
  dock_gates: "Ворота пандусов: склад, пандус, зона и номер. Создаются полем «Количество ворот» в карточке пандуса.",
  suppliers: "Поставщики: от кого поступает товар. Карточки с контактами, договорами и адресами.",
  clients: "Клиенты-поклажедатели: чей товар хранится на складе. Доступ в кабинет — в «Доступ в кабинет».",
  "/reports": "Отчёты по остаткам, движениям, срокам годности и работе склада.",
  "/catalog": "Все справочники: товары, контрагенты, упаковки, единицы и прочее.",
  "/company": "Реквизиты вашей компании, сотрудники, приглашения и роли.",
  "/admin": "Все настройки: пользователи, роли, склады, интеграция, журналы.",
  "/settings": "Функциональные опции: включение и выключение блоков системы.",
  "/document-templates": "Макеты документов для просмотра, PDF и печати: версии, проверка, публикация и сверка бухгалтером.",
  "/admin/kz-data-check": "Только чтение: формы контрагентов, прежние ИНН/КПП, неверные и повторяющиеся БИН/ИИН, реквизиты организаций, пояса складов.",
  // UXW-54 (ТЗ «Рабочие столы» 31.10.2026): every item of the sidebar has its «(?)»; these were still without one
  "/documents": "Все поступления, отгрузки и операции склада одним списком: поиск по номеру, фильтры по виду и статусу.",
  operation_documents: "Документы перемещения (C), брака (D) и списания (E): состав, причина и проведение.",
  service_events: "Каждая оказанная клиенту услуга с датой, количеством и тарифом — основа реестра и счёта.",
  service_registers: "Перечень услуг клиента за период: основа счёта и АВР.",
  invoices: "Счета клиентам по реестрам услуг: суммы, статус оплаты и печатная форма.",
  acts: "Акты выполненных работ (АВР) по реестрам услуг: печать и подписание.",
  billing_tariffs: "Цены услуг по договорам клиентов и даты их действия.",
  billing_services: "Перечень услуг склада, которые можно включать в тарифы и счета.",
  signing_packages: "Пакеты документов на подпись ЭЦП или по QR и их статус.",
  billing_disputes: "Вопросы клиентов по счетам и реестрам: что не сходится и чем ответили.",
  client_requests: "Заявки клиентов из кабинета на поступление и отгрузку: проверить, принять или вернуть на уточнение.",
  client_users: "Учётные записи клиентов для входа в кабинет: роли, склады, временный пароль и блокировка.",
};

export const NAV: NavSection[] = [
  {
    code: "inbound",
    label: "Поступление",
    items: [
      { kind: "entity", key: "expected_receipts", label: "Ожидаемые поступления", permission: "inbound.expected_receipts.read" },
      { kind: "page", key: "/dock-schedule", label: "Расписание доков", permission: "inbound.dock_slots.read" },
    ],
  },
  {
    code: "outbound",
    label: "Отгрузка",
    items: [
      { kind: "entity", key: "shipment_orders", label: "Заказы на отгрузку", permission: "outbound.shipment_orders.read" },
      { kind: "entity", key: "waves", label: "Волны отгрузки", permission: "outbound.waves.read", option: "use_waves" },
      { kind: "entity", key: "cargo_units", label: "Грузовые места", permission: "outbound.cargo_units.read", option: "use_cargo_units" },
    ],
  },
  {
    code: "warehouse",
    label: "Склад",
    items: [
      { kind: "page", key: "/work", label: "Работа на складе", permission: "tasks.tasks.read" },
      { kind: "page", key: "/stock-summary", label: "Остатки по товарам", permission: "inventory.stock.read" },
      { kind: "page", key: "/dispatcher", label: "Диспетчер задач", permission: "tasks.dispatch" },
      { kind: "entity", key: "tasks", label: "Задачи", permission: "tasks.tasks.read" },
      { kind: "entity", key: "stock_counts", label: "Инвентаризации", permission: "inventory.stock_counts.read" },
      { kind: "entity", key: "stock_movements", label: "Движения по складу", permission: "inventory.stock_movements.read" },
      // ТЗ 1.2 W02: an additional entry next to the separate lists, never instead of them
      { kind: "page", key: "/documents", label: "Журнал документов", permission: "inbound.expected_receipts.read", option: "use_document_journal" },
      // ТЗ 1.2 раздел 3.4: documents of warehouse operations C / D / E next to the free operations, never instead
      { kind: "entity", key: "operation_documents", label: "Операции склада", permission: "operations.documents.read", option: "use_operation_documents" },
    ],
  },
  {
    code: "topology",
    label: "Топология",
    items: [
      { kind: "page", key: "/topology", label: "Схема склада", permission: "topology.locations.read" },
      { kind: "entity", key: "locations", label: "Места хранения", permission: "topology.locations.read" },
      { kind: "entity", key: "zones", label: "Зоны склада", permission: "topology.zones.read" },
      { kind: "entity", key: "docks", label: "Пандусы и ворота", permission: "topology.docks.read", option: "use_docks" },
      // UX3010-08: the gates as a catalogue of their own (warehouse, dock, zone, number), next to their docks
      { kind: "entity", key: "dock_gates", label: "Ворота", permission: "topology.docks.read", option: "use_docks" },
    ],
  },
  {
    code: "analytics",
    label: "Отчёты",
    items: [{ kind: "page", key: "/reports", label: "Отчёты", permission: "analytics.reports.read" }],
  },
  // ТЗ 1.2 NAV-03 (29.10.2026): new groups are added after the old ones, nothing above moves or changes
  {
    code: "billing",
    label: "Услуги и счета",
    items: [
      // UX3010-09 (30.10.2026): completed operations of a client → the register of the chosen ones; first in the group
      { kind: "page", key: "/billing/operations", label: "Операции к расчёту", permission: "billing.services.read", option: "use_billing" },
      { kind: "entity", key: "service_events", label: "Журнал услуг", permission: "billing.services.read", option: "use_billing" },
      { kind: "entity", key: "service_registers", label: "Реестры услуг", permission: "billing.registers.read", option: "use_billing" },
      { kind: "entity", key: "invoices", label: "Счета на оплату", permission: "billing.invoices.read", option: "use_billing" },
      { kind: "entity", key: "acts", label: "АВР", permission: "billing.acts.read", option: "use_billing" },
      { kind: "entity", key: "billing_tariffs", label: "Тарифы", permission: "billing.tariffs.read", option: "use_billing" },
      { kind: "entity", key: "billing_services", label: "Каталог услуг", permission: "billing.catalog.read", option: "use_billing" },
      { kind: "entity", key: "signing_packages", label: "Подписание", permission: "billing.archive.read", option: "use_signing" },
      { kind: "entity", key: "billing_disputes", label: "Обращения на сверку", permission: "billing.disputes.read", option: "use_billing" },
    ],
  },
  {
    code: "customers",
    label: "Клиенты",
    items: [
      { kind: "entity", key: "client_requests", label: "Заявки клиентов", permission: "customers.requests.read", option: "use_portal" },
      { kind: "entity", key: "client_users", label: "Доступ в кабинет", permission: "customers.users.read", option: "use_portal" },
    ],
  },
  {
    code: "catalog",
    label: "Справочники",
    items: [
      { kind: "page", key: "/catalog", label: "Все справочники", permission: "nsi.storage_objects.read" },
      // UX3010-13: our organizations — the side of documents, contracts and invoices (ADR 0018)
      {
        kind: "entity",
        key: "companies",
        label: "Мои организации",
        permission: "core.companies.read",
        help: "Наши организации: реквизиты, контакты, банк и префикс нумерации. Рабочая организация выбирается в шапке.",
      },
      // UX3010-08: suppliers and clients — catalogues of their own over the same partner rows
      { kind: "entity", key: "suppliers", label: "Поставщики", permission: "nsi.partners.read" },
      { kind: "entity", key: "clients", label: "Клиенты", permission: "nsi.partners.read" },
    ],
  },
  {
    code: "administration",
    label: "Администрирование",
    items: [
      { kind: "page", key: "/company", label: "Моя компания", permission: "core.users.read" },
      { kind: "page", key: "/admin", label: "Все настройки", permission: "core.settings.read" },
      { kind: "page", key: "/settings", label: "Функциональные опции", permission: "core.settings.read" },
      // KZ-DOC TMP01 (30.10.2026): added after the existing items; «Шаблоны этикеток» stays in «Все настройки»
      { kind: "page", key: "/document-templates", label: "Шаблоны документов", permission: "document_templates.read" },
      // KZ 30.10.2026 (ТЗ реквизитов §15.1): a read-only check of the data; the right is assigned by the administrator
      { kind: "page", key: "/admin/kz-data-check", label: "Проверка данных под Казахстан", permission: "core.data_check.read" },
    ],
  },
];

/** Registry sections shown on each hub page. */
export const HUBS: Record<string, { title: string; intro: string; sections: string[] }> = {
  catalog: {
    title: "Справочники",
    intro: "Товары, контрагенты, упаковки, штрихкоды и устройство склада. Начните с поиска по названию.",
    sections: ["nsi", "topology"],
  },
  admin: {
    title: "Администрирование",
    intro: "Пользователи и права, печать, интеграция с ERP, очереди задач и журналы.",
    sections: ["admin", "resources", "integration", "inventory"],
  },
};


/** GET /topology/map: the warehouse plan. */
export interface MapCell {
  id: string;
  code: string;
  level: number | null;
  position: number | null;
  zone_id: string | null;
  state_id: string | null;
  type_id: string;
  rack_id: string | null;
  is_active: boolean;
  quantity: string;
  planned_out: string;
  objects: number;
  /** Pallets / boxes standing in the cell. */
  nested: number;
  /** Percent of the type capacity from the fill register, when the type controls capacity. */
  fill_percent: number | null;
  color: string | null;
}

/** GET /locations/{id}/contents */
export interface LocationContents {
  id: string;
  code: string;
  placeable: boolean;
  path: { id: string; code: string }[];
  stock: { storage_object_id: string; code: string; name: string; quantity: string; planned_out: string; batch_id: string | null }[];
  nested: { id: string; code: string; type: string; quantity: string; objects: number; children: number }[];
}

export interface MapRack {
  id: string;
  code: string;
  name: string;
  room_id: string;
  aisle_id: string | null;
  aisle_code: string | null;
  kind: string;
  /** Metres on the warehouse plan; null = not placed yet (laid out automatically). */
  x: string | null;
  y: string | null;
  levels: number;
  positions: number;
  /** Real cell size in metres: along the rack, deep, tall (from the cells' type or defaults). */
  cell_length: string;
  cell_width: string;
  cell_height: string;
  /** Rack depth in metres when set on the rack itself. */
  depth: string | null;
  /** False when the sizes are defaults because the cell type has none. */
  sized_by_type: boolean;
  cells: MapCell[];
}

export interface MapRoom {
  id: string;
  code: string;
  name: string;
  racks: number;
  /** Metres: origin on the plan and the floor size; null = unknown. */
  x: string | null;
  y: string | null;
  length: string | null;
  width: string | null;
  height: string | null;
}

/** A zone of the plan: its kind and temperature give its colour, `x`/`y`/`length`/`width` its area in metres (none —
 * the plan draws it around the racks of its cells). */
export interface MapZone {
  id: string;
  code: string;
  name: string;
  kind: string;
  temperature_min?: string | number | null;
  temperature_max?: string | number | null;
  x?: string | number | null;
  y?: string | number | null;
  length?: string | number | null;
  width?: string | number | null;
}

export interface WarehouseMap {
  warehouse_id: string;
  room_id: string | null;
  rooms: MapRoom[];
  zones: MapZone[];
  states: { id: string; code: string; name: string; kind: string; allow_put: boolean; allow_pick: boolean }[];
  racks: MapRack[];
  loose: MapCell[];
  cells: number;
  occupied: number;
  /** 3D docks (29.10.2026): ramps with their gates; drawn only with the option «use_docks». */
  docks?: MapDock[];
}

export * from "./docks";


/** Russian labels for status codes that reach the screen outside of enum-described fields (events, hints, messages). */
export const STATUS_LABELS: Record<string, string> = {
  new: "Новый",
  draft: "Черновик",
  confirmed: "Подтверждён",
  staged: "В экспедиции",
  planning: "Планирование сборки",
  planned: "Запланирован",
  picking: "Собирается",
  picked: "Собран",
  packed: "Упакован",
  shipped: "Отгружен",
  in_progress: "В работе",
  accepted: "Принято",
  closed: "Закрыт",
  cancelled: "Отменён",
  ready: "К выполнению",
  received: "Получена",
  waiting: "Ожидание",
  problem: "Проблема",
  blocked: "Заблокирован",
  done: "Выполнено",
  arrived: "Прибыл",
  open: "Формируется",
  counted: "Посчитано",
  approved: "Утверждена",
  applied: "Применён",
  rejected: "Отклонён",
  validated: "Проверен",
  pending: "Ожидает",
  queued: "В очереди",
  sent: "Отправлено",
  failed: "Ошибка",
  error: "Ошибка",
  ok: "Успешно",
  skipped: "Пропущено",
  printed: "Распечатан",
  active: "Активен",
  in_stock: "На складе",
  written_off: "Списан",
  available: "Доступна",
  reserved: "Зарезервировано",
  // CW31: the outcome of a billing dispute in the toast of «Закрыть обращение»; a cargo unit of a partly shipped order
  resolved: "Решено",
  partly_shipped: "Частично отгружено",
  declined: "Отклонено",
};

export function statusLabel(code: unknown): string {
  if (code === null || code === undefined) return "";
  const key = String(code);
  return STATUS_LABELS[key] ?? key;
}

const EVENT_SUBJECTS: Record<string, string> = {
  expected_receipt: "Поступление",
  shipment_order: "Заказ",
  task: "Задача",
  task_list: "Лист задач",
  dock_slot: "Слот на воротах",
  cargo_unit: "Грузовое место",
  kit: "Комплект",
  stock: "Остатки",
  stock_count: "Инвентаризация",
  location: "Ячейка",
  zone: "Зона",
  wave: "Волна",
  mx: "Паллета / МХ",
  print: "Печать",
  replenishment: "Пополнение",
  notification: "Уведомление",
  webhook: "Webhook",
  integration: "Интеграция",
  draft: "Черновик документа",
  client_request: "Заявка клиента",
  billing: "Услуги и счета",
  signing: "Подписание",
  operation_document: "Документ операции",
};
const EVENT_ACTIONS: Record<string, string> = {
  status_changed: "смена статуса",
  created: "создано",
  closed: "закрыто",
  done: "выполнено",
  planned: "спланировано",
  shipping_planned: "спланирована отгрузка",
  compaction_planned: "спланировано уплотнение",
  shipped: "отгружено",
  received: "принято",
  discrepancy: "расхождения при приёмке",
  problem: "проблема",
  chained: "вторая часть транзита",
  assembled: "собран",
  disassembled: "разобран",
  adjusted: "корректировка",
  state_changed: "смена состояния",
  counted: "посчитано",
  approved: "утверждена",
  tasks: "созданы задачи",
  blocked: "заблокирована",
  moved: "перемещена",
  queued: "поставлена в очередь",
  cancelled: "отменена",
  test: "проверка",
  document_applied: "документ применён",
  draft_received: "получен черновик",
  control_planned: "спланирован контроль",
  line_corrected: "исправлена строка",
  staged: "передан в экспедицию",
  short_pick: "недобор при отборе",
  control_discrepancy: "расхождение при контроле",
  recount_remaining: "пересчёт оставшихся строк",
  posted: "проведён",
};

/**
 * QA1101-B01: the words of every event the server writes — the same list as `common/catalog.py: EVENT_TYPES` (a test
 * of the server keeps that list complete). An event of a later release without words here is still named by its
 * subject and action words, and never by its code.
 */
export const EVENT_LABELS: Record<string, string> = {
  "expected_receipt.status_changed": "Поступление: смена статуса",
  "expected_receipt.received": "Поступление: приёмка выполнена",
  "expected_receipt.discrepancy": "Поступление: принято с расхождениями",
  "expected_receipt.planned": "Поступление: размещение спланировано",
  "expected_receipt.control_planned": "Поступление: контроль спланирован",
  "expected_receipt.line_corrected": "Поступление: строка исправлена",
  "expected_receipt.control_discrepancy": "Поступление: расхождение при контроле",
  "shipment_order.status_changed": "Заказ: смена статуса",
  "shipment_order.planned": "Заказ: отбор спланирован",
  "shipment_order.shipping_planned": "Заказ: отгрузка спланирована",
  "shipment_order.shipped": "Заказ: отгружен",
  "shipment_order.control_planned": "Заказ: контроль спланирован",
  "shipment_order.line_corrected": "Заказ: строка исправлена",
  "shipment_order.short_pick": "Заказ: недобор при отборе",
  "shipment_order.staged": "Заказ: передан в экспедицию",
  "shipment_order.control_discrepancy": "Заказ: расхождение при контроле",
  "shipment_order.control_resolved": "Заказ: решение ответственного по расхождению контроля",
  "task.status_changed": "Задача: смена статуса",
  "task.done": "Задача: выполнена",
  "task.problem": "Задача: проблема",
  "task.chained": "Задача: создана вторая часть транзита",
  "task_list.created": "Лист задач: создан",
  "stock.adjusted": "Остатки: корректировка",
  "stock.state_changed": "Остатки: смена состояния",
  "stock_count.tasks": "Инвентаризация: созданы задачи",
  "stock_count.counted": "Инвентаризация: посчитано",
  "stock_count.approved": "Инвентаризация: утверждена",
  "stock_count.recount_remaining": "Инвентаризация: пересчёт оставшихся строк",
  "replenishment.planned": "Пополнение: спланировано",
  "wave.planned": "Волна: спланирована",
  "wave.done": "Волна: выполнена",
  "wave.cancelled": "Волна: отменена",
  "zone.compaction_planned": "Зона: уплотнение спланировано",
  "location.blocked": "Ячейка: заблокирована",
  "mx.created": "Паллета / МХ: создана",
  "mx.moved": "Паллета / МХ: перемещена",
  "cargo_unit.created": "Грузовое место: создано",
  "cargo_unit.closed": "Грузовое место: закрыто",
  "dock_slot.status_changed": "Слот на воротах: смена статуса",
  "kit.assembled": "Комплект: собран",
  "kit.disassembled": "Комплект: разобран",
  "print.queued": "Печать: задание поставлено в очередь",
  "integration.draft_received": "Интеграция: получен черновик",
  "integration.document_applied": "Интеграция: документ применён",
  "webhook.test": "Webhook: проверка",
  "operation_document.status_changed": "Документ операции: смена статуса",
  "operation_document.posted": "Документ операции: проведён",
  "client_request.submitted": "Заявка клиента: отправлена на склад",
  "client_request.in_review": "Заявка клиента: взята на проверку",
  "client_request.needs_clarification": "Заявка клиента: нужно уточнение",
  "client_request.accepted": "Заявка клиента: принята",
  "client_request.rejected": "Заявка клиента: отклонена",
  "client_request.change_requested": "Заявка клиента: запрошено изменение",
  "client_request.cancel_requested": "Заявка клиента: запрошена отмена",
  "client_request.change_resolved": "Заявка клиента: решение по запросу",
  "billing.event_corrected": "Журнал услуг: строка исправлена",
  "billing.register_created": "Реестр услуг: создан",
  "billing.register_approved": "Реестр услуг: утверждён",
  "billing.invoice_created": "Счёт: создан",
  "billing.invoice_issued": "Счёт: выставлен",
  "billing.invoice_delivered": "Счёт: передан клиенту",
  "billing.invoice_corrected": "Счёт: исправлен отдельным документом",
  "billing.invoice_cancelled": "Счёт: отменён",
  "billing.payment_recorded": "Оплата: записана",
  "billing.act_created": "АВР: создан",
  "billing.act_frozen": "АВР: зафиксирован для подписи",
  "billing.act_revised": "АВР: исправлен",
  "billing.act_cancelled": "АВР: отменён",
  "signing.package_created": "Подписание: пакет создан",
  "signing.invited": "Подписание: приглашение отправлено",
  "signing.invitation_revoked": "Подписание: приглашение отозвано",
  "signing.party_signed": "Подписание: сторона подписала",
  "signing.rejected": "Подписание: отказ от подписи",
};

/** "shipment_order.status_changed" → "Заказ: смена статуса"; a code without words is never shown («Заказ: событие»). */
export function eventLabel(type: string): string {
  if (EVENT_LABELS[type]) return EVENT_LABELS[type];
  const [subject, ...rest] = type.split(".");
  const action = rest.join(".");
  const s = EVENT_SUBJECTS[subject] ?? "Событие";
  const a = EVENT_ACTIONS[action] ?? "событие";
  return action && (s !== "Событие" || a !== "событие") ? `${s}: ${a}` : s;
}


// ---- ТЗ 1.2 (29.10.2026): the journal of documents (W02) and the client cabinet (C01–C06) ----------------------
/** One row of GET /documents/journal: a document or a projected fact with its type, colour and next step. */
export interface JournalRow {
  id: string;
  type: "expected_receipt" | "shipment_order" | "stock_count" | "transfer" | "defect" | "write_off";
  type_label: string;
  operation: "receive" | "ship" | "move" | "writeoff" | "count";
  entity: string;
  number: string;
  at: string;
  date: string;
  partner_id: string | null;
  partner_name?: string | null;
  warehouse_id: string;
  warehouse_code?: string | null;
  status: string;
  status_label: string;
  quantity: string;
  quantity_fact: string;
  unit: string;
  products: number;
  next_step: string;
  author: string | null;
  responsible: string | null;
  path: string;
  /** 5.2 «номер основания»: the ERP number / external id of a planning document, the basis of an operation document. */
  basis?: string | null;
  /** Documents of operations C / D / E (ТЗ 1.2 раздел 3.4): whose goods and why. */
  owner_kind?: "client" | "operator" | null;
  reason?: string | null;
}

export interface JournalResponse {
  items: JournalRow[];
  total: number;
  page: number;
  size: number;
  types: { value: string; label: string; operation: string }[];
  period: { from: string; to: string };
  truncated: boolean;
  /** 5.2: the bulk actions the journal offers (confirm / post / close / cancel) with their consequences in words. */
  bulk_actions?: { value: string; label: string; outcome: string }[];
}

/** One selected row of the journal, as POST /documents/journal/bulk/{preview,execute} takes it. */
export interface JournalBulkItem {
  type: JournalRow["type"];
  id: string;
}

/** POST /documents/journal/bulk/preview: allowed / reason per document and the summary before anything runs. */
export interface JournalBulkPreview {
  action: string;
  label: string;
  items: {
    type: string;
    id: string;
    number: string | null;
    status_label?: string;
    allowed: boolean;
    reason: string | null;
    outcome?: string;
    action_label?: string;
  }[];
  summary: {
    total: number;
    allowed: number;
    blocked: number;
    by_type: { type: string; label: string; count: number }[];
    outcome: string;
    needs_reason: boolean;
  };
}

/** POST /documents/journal/bulk/execute: the result of every document; failed ones can be retried alone. */
export interface JournalBulkResult {
  action: string;
  label: string;
  items: { type: string; id: string; number?: string | null; ok: boolean; error: string | null; status?: string; status_label?: string }[];
  done: number;
  failed: number;
}

/** GET /portal/auth/me */
export interface PortalMe {
  user: { id: string; username: string; full_name: string; roles: string[] };
  partner: { id: string; name: string; bin: string | null; kind: string | null };
  permissions: string[];
  warehouses: { id: string; code: string; name: string }[];
  must_change_password: boolean;
  visibility: string;
  feature_options: Record<string, boolean>;
}

export interface PortalDashboard {
  receipts: { expected: number; in_progress: number; accepted: number };
  orders: { new: number; picking: number; ready: number; shipped: number };
  requests: Record<string, number>;
  stock: { unit: string; available: string }[];
  stock_products: number;
  invoices: { new: number; total: number };
  to_sign: number;
}

/**
 * ТЗ «Рабочие столы и персонализация» 31.10.2026 (UXW-10…26): the own look of a principal — palette ids and the order
 * of the widgets only (`GET/PUT /auth/me/workspace` for staff, `/portal/me/workspace` for a person of the cabinet).
 */
export type WorkspaceCabinet = "staff" | "portal";
export type WidgetSize = "half" | "full";

export interface WorkspaceAppearance {
  /** A palette id or «standard» — the neutral background of the theme (the default of staff). */
  background: string;
  accent: string;
}

export interface WorkspaceWidgetPlacement {
  code: string;
  size: WidgetSize;
}

export interface WorkspaceSection {
  schema_version: number;
  /** UXW-17: sent back with a save; another window that saved in between makes the server answer 409. */
  revision: number;
  appearance: WorkspaceAppearance;
  desktop: { widgets: WorkspaceWidgetPlacement[] };
  updated_at: string | null;
}

export interface WorkspaceCatalogItem {
  code: string;
  label: string;
  help: string;
  size: WidgetSize;
  sizes: WidgetSize[];
  /** On the standard desktop of this cabinet. */
  default: boolean;
}

export interface WorkspacePaletteBackground {
  id: string;
  label: string;
  hex: string;
}

export interface WorkspacePaletteAccent {
  id: string;
  label: string;
  hex: string;
  text: string;
}

export interface WorkspacePayload {
  cabinet: WorkspaceCabinet;
  workspace: WorkspaceSection;
  /** false — nothing saved yet, `workspace` is the standard one. */
  saved: boolean;
  standard: { appearance: WorkspaceAppearance; desktop: { widgets: WorkspaceWidgetPlacement[] } };
  palette: { backgrounds: WorkspacePaletteBackground[]; accents: WorkspacePaletteAccent[] };
  /** The widgets this principal may place now; others are not named (UXW-20). */
  catalog: WorkspaceCatalogItem[];
  /** Codes of the saved desktop not available now (a right was taken away): kept, never drawn (CW-18). */
  hidden: string[];
}

export interface WorkspaceUpdate {
  revision: number;
  /** `null` — back to the standard look; absent — unchanged. */
  appearance?: WorkspaceAppearance | null;
  desktop?: { widgets: WorkspaceWidgetPlacement[] } | null;
}

/**
 * ТЗ «Рабочие столы» 31.10.2026 (UXW-27…31, CW-20…25): one basis of «Требует внимания» — `GET /dashboard/attention`
 * for staff, `GET /portal/attention` in the client cabinet. The target is a record (entity + id), never a URL: the
 * screen opens it through its own whitelist of routes.
 */
export type AttentionLevel = "danger" | "warning" | "info";

export interface AttentionItem {
  /** Stable: «<kind>:<id of the record>». */
  id: string;
  kind: string;
  level: AttentionLevel;
  /** What happened, in short words. */
  reason: string;
  /** `number` — of a document or a task only; a batch, a zone or a slot is named by `name` with its real `type`. */
  object: { entity: string; id: string; type: string; number: string | null; name: string | null };
  warehouse: { id: string; code: string } | null;
  status: string;
  /** The next step in words («Открыть поступление»). */
  next_action: string;
  /** A date or a moment (ISO), or null. */
  due: string | null;
  /** The document a task or a slot stands on — only when the user may open it. */
  basis: { entity: string; id: string; number: string } | null;
  target: { entity: string; id: string; tab: string | null } | null;
}

export interface AttentionGroup {
  kind: string;
  label: string;
  level: AttentionLevel;
  /** Every visible row of the kind — the same set the rows are taken from. */
  count: number;
}

export interface AttentionPage {
  generated_at: string;
  warehouse_id?: string | null;
  kind: string | null;
  total: number;
  offset: number;
  limit: number;
  groups: AttentionGroup[];
  items: AttentionItem[];
  /** The rows shown are not all of `total`. */
  truncated: boolean;
}

/** CW31-B21: a package of a catalog product — «Коробка», 50 base units in it. */
export interface PortalProductPackage {
  id: string;
  code: string;
  name: string;
  ratio: string;
  is_base: boolean;
}

/** GET /portal/products: the products a client may name in a request — its own cards and the common catalog. */
export interface PortalProduct {
  id: string;
  code: string;
  name: string;
  article?: string | null;
  gtin?: string | null;
  unit: string;
  /** CW31-B01: digits after the comma the unit allows — 0 for pieces, 3 for kilograms. */
  unit_decimals?: number;
  /** CW31-B19: a card of the common catalog (no owner). */
  shared?: boolean;
  packages?: PortalProductPackage[];
}

export interface PortalRequestLine {
  line_no?: number;
  storage_object_id: string | null;
  product_text: string | null;
  package_id?: string | null;
  packages_count?: string | null;
  quantity: string;
  batch_code?: string | null;
  expiry_date?: string | null;
  comment?: string | null;
}

export interface PortalRequest {
  id: string;
  number: string;
  kind: "inbound" | "outbound";
  status: string;
  revision: number;
  warehouse_id: string;
  expected_at: string | null;
  supplier_name: string | null;
  supplier_invoice: string | null;
  recipient_name: string | null;
  recipient_bin?: string | null; // CW31: the БИН/ИИН the warehouse matches the recipient by
  delivery_kind: string | null;
  delivery_address: string | null;
  comment: string | null;
  operator_comment: string | null;
  document_type: string | null;
  document_id: string | null;
  document_number: string | null;
  submitted_at: string | null;
  reviewed_at: string | null;
  change_request: Record<string, unknown> | null;
  created_at: string;
  lines?: PortalRequestLine[];
}

export const PORTAL_REQUEST_STATUSES: Record<string, string> = {
  draft: "Черновик",
  submitted: "Отправлена",
  in_review: "На проверке",
  accepted: "Принята",
  needs_clarification: "Нужно уточнить",
  rejected: "Отклонена",
  cancel_requested: "Запрошена отмена",
  cancelled: "Отменена",
};

export interface PortalDocument {
  id: string;
  number: string;
  date: string;
  status: string;
  warehouse_id: string;
  planned_at: string | null;
  /** Plain sums of every line; when `mixed_units` they add different units and say nothing — read `totals_by_unit`. */
  quantity_plan: string;
  quantity_fact: string;
  comment: string | null;
  /** CW31-B02: the totals per unit («160 шт · 2,5 кг») and the progress by lines (a line is done when fact ≥ plan). */
  totals_by_unit?: PortalUnitTotal[];
  lines_total?: number;
  lines_done?: number;
  mixed_units?: boolean;
}

/** CW31-B02: one unit of a document of the cabinet — plan and fact in that unit only. */
export interface PortalUnitTotal {
  unit: string;
  plan: string;
  fact: string;
  lines: number;
  lines_done: number;
}

export interface PortalDocumentDetail extends PortalDocument {
  kind: "receipts" | "orders";
  cancelled: boolean;
  request: { id: string; number: string; status: string } | null;
  timeline: { code: string; label: string; state: "done" | "current" | "next" | "skipped"; at: string | null }[];
  lines: { line_no: number; product: { id: string; code: string; name: string }; unit: string; quantity_plan: string; quantity_fact: string; quantity_picked?: string | null }[];
}

export interface PortalStockRow {
  product: { id: string; code: string; name: string; article: string | null };
  unit: string;
  batch: string | null;
  expiry_date: string | null;
  state: string | null;
  state_kind: string;
  physical: string;
  /** What a new order may take: physical − reserved − collected − quarantine (FULL0211-B08). */
  available: string;
  /** Put aside by orders and warehouse tasks, still in storage. */
  reserved: string;
  /** FULL0211-B08: picked for orders and lying in the shipping, expedition or packing zones until shipped. */
  collected: string;
  quarantine: string;
  places: string[];
}

export interface PortalInvoice {
  id: string;
  number: string;
  date: string;
  due_date: string | null;
  currency: string;
  status: string;
  payment_status: string;
  total_net: string;
  total_tax: string;
  total_gross: string;
  paid_amount: string;
  delivered_at: string | null;
  /** CW31-B09: `unit_label` («шт») next to the stable `unit_code` («piece»). */
  lines?: { line_no: number; description: string; quantity: string; unit_code: string; unit_label?: string; rate: string; net: string; tax: string; gross: string }[];
}

/** CW31-B10: GET /portal/finance/disputes — an own dispute with the answer of the warehouse. */
export interface PortalDispute {
  id: string;
  document_type: "invoice" | "act" | "register" | string;
  document_id: string;
  document_number: string | null;
  /** «АВР J000001», «Счёт H000001», «Реестр услуг G000001». */
  document_label: string;
  line_id: string | null;
  reason: string;
  status: string;
  status_label: string;
  resolution: string | null;
  created_at: string;
  resolved_at: string | null;
  /** The name of the employee who decided, never the login. */
  resolved_by_name: string | null;
}

export interface SigningPartyView {
  side: "operator" | "client";
  status: string;
  signed_at: string | null;
  signer_name: string | null;
  verification_status: string;
  reject_reason: string | null;
}

/** The limited view of a signing package: GET /signing/link/{token}, the `package` of a portal act. */
export interface SigningPackageView {
  package_id: string;
  version: number;
  status: string;
  package_hash: string;
  hashes: Record<string, string>;
  act: Record<string, unknown> | null;
  register: Record<string, unknown> | null;
  parties: SigningPartyView[];
  can_sign: boolean;
  why_not: string | null;
  expires_at: string | null;
  /** CW31-B16: «operator» in the staff screen «Подписать от исполнителя» (can_sign / why_not are of our side). */
  side?: "operator" | "client";
}

/** CW31-B16: one check before the signature of our side, with the place to fix it for a role that may. */
export interface OperatorSigningCheck {
  code: "right" | "package" | "party" | "order" | "requisites" | "provider" | string;
  label: string;
  ok: boolean;
  text: string;
  fix_path: string | null;
  fix_label: string | null;
}

/** CW31-B16: GET /signing/acts/{act_id}/operator — the screen «Подписать от исполнителя» of the current version. */
export interface OperatorSigning {
  act: { id: string; number: string; status: string; date: string; path: string };
  package_id: string | null;
  view: SigningPackageView | null;
  checks: OperatorSigningCheck[];
  providers: { code: string; label: string; available: boolean; reason: string | null }[];
  can_sign: boolean;
  why_not: string | null;
}

export interface PortalAct {
  id: string;
  number: string;
  date: string;
  status: string;
  currency: string;
  total_gross: string;
  invoice_id: string;
  register_id: string | null;
  document_hash: string | null;
  service_from: string | null;
  service_to: string | null;
  package: SigningPackageView | null;
}

export const SIGNING_STATUS_LABELS: Record<string, string> = {
  ready: "Готово к подписанию",
  invited: "Приглашение создано",
  awaiting: "Ожидаем подпись",
  verifying: "Проверяем подпись",
  partially_signed: "Подписано одной стороной",
  signed: "Подписано обеими сторонами",
  rejected: "Отказ",
  expired: "Срок истёк",
  cancelled: "Отменено",
  superseded: "Заменено новой версией",
  pending: "Ожидает",
  unknown: "Результат неизвестен",
  failed: "Ошибка",
};

/** UX3010-09 (30.10.2026) «Операции к расчёту»: GET /billing/operations. Money and quantities are decimal strings. */
export interface BillingAmount {
  currency: string;
  net: string;
  tax: string;
  gross: string;
}

export interface BillingOperationHandled {
  handling_type: string;
  label: string;
  quantity: string;
  unit: string;
  base_quantity?: string;
  base_unit?: string;
}

/** One service an operation gives: written already (`event_id`) or only previewed by the same rules. */
export interface BillingOperationCharge {
  service_id: string;
  service_code: string | null;
  service_name: string | null;
  unit_code: string;
  unit_label: string;
  quantity: string;
  handling_type: string;
  handling_label: string;
  /** The day of the service in the warehouse time zone — the tariff of this day applies. */
  date: string;
  rate: string | null;
  currency: string | null;
  tax_mode: string;
  tax_label: string;
  tax_rate: string | null;
  net: string | null;
  tax: string | null;
  gross: string | null;
  tariff_id: string | null;
  tariff_label: string | null;
  formula: string | null;
  state: "new" | "recorded" | "in_register" | "pending" | "other_client" | "cancelled";
  includable: boolean;
  problem: string | null;
  reason: string | null;
  event_id: string | null;
  register: { id: string; number: string; status: string } | null;
}

export interface BillingOperation {
  /** The key to send back in POST /billing/operations/register: «receipt:<id>», «shipment:<id>», «task:<id>», «manual:<id>»… */
  key: string;
  kind: "receipt" | "shipment" | "task" | "manual" | "storage" | "correction";
  kind_label: string;
  /** The operation for the colour tag (receipt, shipment, placement, move_mx…). */
  operation: string;
  number: string | null;
  path: string | null;
  occurred_at: string;
  local_date: string;
  local_time: string;
  timezone: string;
  warehouse_id: string | null;
  warehouse_code: string | null;
  warehouse_name: string | null;
  handled: BillingOperationHandled[];
  charges: BillingOperationCharge[];
  /** The flag is active: at least one service of the operation may go to a register. */
  selectable: boolean;
  /** Why the flag is off: no_service | in_register | no_tariff | ambiguous | unit_mismatch | no_owner | … */
  problem: string | null;
  reason: string | null;
  notes: string[];
  registers: { id: string; number: string; status: string }[];
  amounts: BillingAmount[];
}

export interface BillingOperationsResponse {
  company: { id: string; name: string };
  partner: { id: string; name: string; bin: string | null } | null;
  period: { from: string; to: string };
  register: { id: string; number: string; status: string } | null;
  items: BillingOperation[];
  truncated: boolean;
  empty_reason: string | null;
  totals: {
    operations: number;
    selectable: number;
    amounts: BillingAmount[];
    selected: { operations: number; amounts: BillingAmount[] } | null;
  };
}

/** POST /billing/operations/register: the draft register of the chosen operations. */
export interface BillingOperationsRegister {
  id: string;
  number: string;
  status: string;
  lines: number;
  operations: number;
  warnings: { kind: string; event_id: string; source: string | null; operation: string; text: string }[];
  currency: string;
  period_from: string;
  period_to: string;
  total_net: string;
  total_tax: string;
  total_gross: string;
  open: { entity: string; id: string };
  idempotent: boolean;
}

/** GET /portal/tariffs (ПР20): the client sees its price, never changes it. */
export interface PortalTariff {
  id: string;
  service: string;
  service_code: string;
  personal: boolean;
  handling_type: string;
  handling_label: string;
  unit_code: string;
  unit_label: string;
  rate: string;
  currency: string;
  tax_mode: string;
  tax_label: string;
  tax_rate: string | null;
  minimum_amount: string | null;
  discount_percent: string | null;
  effective_from: string;
  effective_to: string | null;
  current: boolean;
}
